import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Button,
  Box,
  Collapse,
  Text,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTouchInteraction } from './hooks/useTouchInteraction';
import { getServiceLinks, getProductLinks } from '../../constants/navigationData';

// Static product mapping for specific routes
const staticProductRoutes = {
  'ossp': '/products/ossp',
  'insightdb': '/products/insightdb',
  'ebs': '/products/ebs',
  'queuepro': '/products/queuepro',
  'smart-delivery-platform': '/products/smart-delivery-platform',
  // 'digital-corporate-banking': '/products/digital-corporate-banking',
  'insight-db': '/products/insightdb',
  'queue-pro': '/products/queuepro',
  'one-stop-service-platform': '/products/ossp',
  'enterprise-business-solutions': '/products/ebs',
  'business-intelligence-database': '/products/insightdb',
  'queue-management-system': '/products/queuepro',
  'ecommerce-platform': '/products/smart-delivery-platform'
};

// Function to get product route based on title or ID
const getProductRoute = (product) => {
  // Check if it's a static product by title (case insensitive)
  const titleKey = product.title?.toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  if (staticProductRoutes[titleKey]) {
    return staticProductRoutes[titleKey];
  }
  
  // Check by common name variations
  const title = product.title?.toLowerCase() || '';
  if (title.includes('ossp') || title.includes('one stop')) {
    return '/products/ossp';
  }
  if (title.includes('queue')) {
    return '/products/queuepro';
  }
  if (title.includes('ebs') || title.includes('enterprise')) {
    return '/products/ebs';
  }
  if (title.includes('insight') || title.includes('business intelligence')) {
    return '/products/insightdb';
  }
  if (title.includes('smart delivery') || title.includes('ecommerce')) {
    return '/products/smart-delivery-platform';
  }
  // if (title.includes('digital') && title.includes('banking')) {
  //   return '/products/digital-corporate-banking';
  // }
  
  // Default to generic product details route for API products
  return `/product-details/${product.id}`;
};

const MobileDrawer = ({ 
  isOpen, 
  onClose, 
  activeDropdown, 
  toggleMobileDropdown,
  isCareersPage,
  careersNav,
  navigationItems,
  textColor,
  hoverColor
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use touch interaction hook for better mobile experience
  useTouchInteraction(isOpen);
  
  // Get static data directly
  const services = getServiceLinks();
  const products = getProductLinks();

  // const textColor = useColorModeValue('gray.700', 'gray.200');
  // const hoverColor = useColorModeValue('#000B8C', 'blue.400');

  return (
    <Drawer 
      isOpen={isOpen} 
      placement="right" 
      onClose={onClose}
      size={{ base: "sm", md: "md" }}
    >
      <DrawerOverlay 
        bg="blackAlpha.600" 
        data-drawer-overlay
        onClick={onClose}
      />
      <DrawerContent
        maxW={{ base: "280px", sm: "320px", md: "400px" }}
        bg="white"
        boxShadow="xl"
      >
        <DrawerCloseButton 
          size="lg"
          top={4}
          right={4}
          _hover={{ bg: "gray.100" }}
        />
        <DrawerHeader 
          borderBottomWidth="1px" 
          borderColor="gray.200"
          py={6}
        >
          <Box 
            as="button" 
            onClick={() => { navigate('/'); onClose(); }} 
            _focus={{ outline: 'none' }}
            _hover={{ opacity: 0.8 }}
          >
            <img 
              src="/png/BA.png" 
              alt="Company Logo" 
              style={{ 
                height: '40px', 
                maxWidth: '120px', 
                objectFit: 'contain' 
              }} 
            />
          </Box>
        </DrawerHeader>

        <DrawerBody py={4}>
          <VStack spacing={2} align="stretch">
            {isCareersPage ? (
              <>
                {careersNav.map((item, idx) => (
                  <Button
                    key={item.name}
                    variant="ghost"
                    w="full"
                    textAlign="left"
                    justifyContent="flex-start"
                    fontWeight="medium"
                    color={textColor}
                    fontSize={{ base: "md", sm: "lg" }}
                    py={{ base: 3, sm: 4 }}
                    px={4}
                    borderRadius="md"
                    _hover={{ color: hoverColor, bg: 'gray.50' }}
                    _active={{ bg: 'gray.100' }}
                    transition="all 0.2s"
                    onClick={() => {
                      item.onClick();
                      onClose();
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
                {/* <Button
                  variant="outline"
                  borderColor={textColor}
                  color={textColor}
                  borderRadius="full"
                  w="full"
                  textAlign="left"
                  justifyContent="flex-start"
                  fontWeight="medium"
                  fontSize="md"
                  _hover={{ bg: 'gray.100', color: hoverColor }}
                  mt={2}
                  onClick={() => {
                    navigate('/login-register?mode=login');
                    onClose();
                  }}
                >
                  Login
                </Button>
                <Button
                  bg="#215560"
                  color="white"
                  borderRadius="full"
                  w="full"
                  textAlign="left"
                  justifyContent="flex-start"
                  fontWeight="bold"
                  fontSize="md"
                  _hover={{ bg: '#1a3d17' }}
                  mt={2}
                  onClick={() => {
                    navigate('/login-register?mode=register');
                    onClose();
                  }}
                >
                  Register
                </Button> */}
              </>
            ) : (
              navigationItems.map((item, index) => (
                <Box key={index}>
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    rightIcon={item.hasDropdown ? <ChevronDownIcon /> : null}
                    w="full"
                    textAlign="left"
                    fontWeight="medium"
                    color={textColor}
                    fontSize={{ base: "md", sm: "lg" }}
                    py={{ base: 3, sm: 4 }}
                    px={4}
                    borderRadius="md"
                    _hover={{ color: hoverColor, bg: 'gray.50' }}
                    _active={{ bg: 'gray.100' }}
                    transition="all 0.2s"
                    onClick={() => {
                      if (item.hasDropdown) {
                        toggleMobileDropdown(index);
                      } else if (item.onClick) {
                        item.onClick(navigate);
                        onClose();
                      }
                    }}
                  >
                    {item.name}
                  </Button>
                  {/* Mobile Dropdown */}
                  <Collapse in={activeDropdown === index} animateOpacity>
                    <VStack spacing={1} align="stretch" pl={6} pr={2} mt={2} mb={2} maxH="300px" overflowY="auto">
                      {item.isMegaMenu ? (
                        item.menuType === 'service' ? (
                          <>
                            {services && services.length > 0 ? (
                              services.map((service, serviceIndex) => {
                                const isCurrentService = location.pathname === service.href;
                                return (
                                  <Button
                                    key={service.id || serviceIndex}
                                    variant="ghost"
                                    size="sm"
                                    justifyContent="flex-start"
                                    textAlign="left"
                                    color={isCurrentService ? hoverColor : "gray.600"}
                                    bg={isCurrentService ? "gray.100" : "transparent"}
                                    fontWeight={isCurrentService ? "bold" : "normal"}
                                    fontSize={{ base: "sm", sm: "md" }}
                                    py={{ base: 2, sm: 3 }}
                                    px={3}
                                    borderRadius="md"
                                    _hover={{ color: hoverColor, bg: 'gray.50' }}
                                    _active={{ bg: 'gray.100' }}
                                    w="full"
                                    transition="all 0.2s"
                                    onClick={() => {
                                      const servicePath = service.href || '/our-services';
                                      navigate(servicePath);
                                      onClose();
                                    }}
                                  >
                                    {service.title || `Service ${serviceIndex + 1}`}
                                  </Button>
                                );
                              })
                            ) : (
                              <Text color="gray.500" pl={4} py={3} fontSize="sm">
                                No services available
                              </Text>
                            )}
                          </>
                        ) : (
                          <>
                            {products && products.length > 0 ? (
                              products.map((product, productIndex) => {
                                const productRoute = product.href || getProductRoute(product);
                                const isCurrentProduct = location.pathname === productRoute;
                                return (
                                  <Button
                                    key={product.id || productIndex}
                                    variant="ghost"
                                    size="sm"
                                    justifyContent="flex-start"
                                    textAlign="left"
                                    color={isCurrentProduct ? hoverColor : "gray.600"}
                                    bg={isCurrentProduct ? "gray.100" : "transparent"}
                                    fontWeight={isCurrentProduct ? "bold" : "normal"}
                                    fontSize={{ base: "sm", sm: "md" }}
                                    py={{ base: 2, sm: 3 }}
                                    px={3}
                                    borderRadius="md"
                                    _hover={{ color: hoverColor, bg: 'gray.50' }}
                                    _active={{ bg: 'gray.100' }}
                                    w="full"
                                    transition="all 0.2s"
                                    onClick={() => {
                                      navigate(productRoute);
                                      onClose();
                                    }}
                                  >
                                    {product.title || `Product ${productIndex + 1}`}
                                  </Button>
                                );
                              })
                            ) : (
                              <Text color="gray.500" pl={4} py={3} fontSize="sm">
                                No products available
                              </Text>
                            )}
                          </>
                        )
                      ) :
                        item.dropdownItems?.map((dropdownItem, dropdownIndex) => (
                          <Button
                            key={dropdownIndex}
                            variant="ghost"
                            size="sm"
                            justifyContent="flex-start"
                            textAlign="left"
                            color="gray.600"
                            fontSize={{ base: "sm", sm: "md" }}
                            py={{ base: 2, sm: 3 }}
                            px={3}
                            borderRadius="md"
                            _hover={{ color: hoverColor, bg: 'gray.50' }}
                            _active={{ bg: 'gray.100' }}
                            w="full"
                            transition="all 0.2s"
                            onClick={() => {
                              // যদি ড্রপডাউন আইটেমে ক্লিক করলে কিছু করতে চান, এখানে নেভিগেশন যুক্ত করুন
                              onClose();
                            }}
                          >
                            {dropdownItem}
                          </Button>
                        ))
                      }
                    </VStack>
                  </Collapse>
                </Box>
              ))
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileDrawer;
